import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState({ phase: "polling", code: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", code: null });
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clear();
          setState({ phase: "paid", code: data.order_code });
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setState({ phase: "timeout", code: null });
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center" data-testid="payment-success-page">
      {state.phase === "polling" && (
        <>
          <Loader2 className="animate-spin text-vermilion" size={40} strokeWidth={1} />
          <p className="mt-8 font-mono text-xs uppercase tracking-[0.3em] text-smoke">Confirming your payment with Stripe…</p>
        </>
      )}
      {state.phase === "paid" && (
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
          <CheckCircle2 className="mx-auto text-vermilion" size={56} strokeWidth={1} />
          <h1 className="mt-8 font-display text-4xl font-light text-bone sm:text-5xl">The objects are yours.</h1>
          <p className="mt-6 text-base font-light text-smoke">
            Your order code is{" "}
            <span className="font-mono text-vermilion" data-testid="success-order-code">{state.code}</span>
            . Keep it close — you will need it to trace the journey.
          </p>
          <div className="mt-10 flex justify-center gap-4">
            <Link to="/track" data-testid="success-track-button" className="bg-vermilion px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] text-obsidian transition-colors hover:bg-vermilion-dark">
              Trace the Order
            </Link>
            <Link to="/shop" className="border border-white/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] text-smoke transition-colors hover:text-bone">
              Keep Browsing
            </Link>
          </div>
        </motion.div>
      )}
      {(state.phase === "timeout" || state.phase === "error") && (
        <>
          <Loader2 className="text-brass" size={40} strokeWidth={1} />
          <h1 className="mt-8 font-display text-4xl font-light text-bone">Still confirming…</h1>
          <p className="mt-6 max-w-md text-base font-light text-smoke">
            Stripe is taking a moment. If you completed payment, your order is safe — check the tracking page shortly or write to the concierge.
          </p>
          <Link to="/track" className="mt-10 inline-block border border-white/20 px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] text-smoke hover:text-bone">
            Go to Tracking
          </Link>
        </>
      )}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center" data-testid="payment-cancel-page">
      <XCircle className="text-faint" size={56} strokeWidth={1} />
      <h1 className="mt-8 font-display text-4xl font-light text-bone sm:text-5xl">The handover was paused.</h1>
      <p className="mt-6 max-w-md text-base font-light text-smoke">
        No payment was taken. Your collection is still waiting in the cart whenever you are ready.
      </p>
      <Link to="/shop" data-testid="cancel-return-button" className="mt-10 bg-vermilion px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] text-obsidian transition-colors hover:bg-vermilion-dark">
        Return to the Catalog
      </Link>
    </div>
  );
}
